import {Action, Module, Mutation, VuexModule} from 'vuex-module-decorators';
import {IEngagementSource, IItemFavorite, ILineItem, OrderStore, MenuStore, mergeAndAdd, ITEM_FAVORITE_TYPE} from '../../store/';
import {IBaseResponse, ItemService, WebService} from '../../services';

export type TMaybeItemFavorite = IItemFavorite | null;
type TLineItemFunction<T> = (arg: ILineItem) => T;

interface IItemFavoriteStoreState {
	favorites: IItemFavorite[];
}

export const FAVORITE_PREFIX = 'MY-';

@Module({name: 'itemFavorites', namespaced: true})
class ItemFavoriteModule extends VuexModule implements IItemFavoriteStoreState {
	favorites: IItemFavorite[] = [];
	sharedFavorites: IItemFavorite[] = [];

	get favorite() {
		return (favoriteId: string): IItemFavorite | null => {
			return this.favorites.find((favorite) => favorite.id === favoriteId) ?? null;
		};
	}

	get sharedFavorite() {
		return (favoriteId: string): IItemFavorite | null => {
			return this.sharedFavorites.find((favorite) => favorite.id === favoriteId) ?? null;
		};
	}

	get favoriteCreatedFrom() {
		return (favoriteId: string): IItemFavorite | null => {
			return this.favorites.find((favorite) => favorite.createdFromId === favoriteId) ?? null;
		};
	}

	get favoriteByCreatedFromId() {
		return (favoriteId: string): IItemFavorite | null => {
			return this.favorites.find((favorite) => favorite.createdFromId === favoriteId) ?? null;
		};
	}

	get favoriteFromEngagementSource(): (engagementSource: IEngagementSource | null) => IItemFavorite | null {
		return (engagementSource: IEngagementSource | null) => {
			if(engagementSource !== null && ItemService.engagementSourceIsFavorite(engagementSource)) {
				return this.favorite(engagementSource.id);
			}
			return null;
		};
	}

	get favoriteByLineItem(): TLineItemFunction<TMaybeItemFavorite> {
		return (lineItem) => {
			return this.favoriteFromEngagementSource(lineItem.engagementSource);
		};
	}

	get favoriteNameExists() {
		return (name: IItemFavorite['name']) => {
			if(this.favorites.length === 0) {
				return false;
			}

			const favNameLC = name.toLowerCase();
			return this.favorites.some(fav => fav.name.toLowerCase() === favNameLC);
		};
	}

	@Action
	async fetchFavorites() {
		const response = await WebService.authenticatedClient.get<IBaseResponse<{favorites: IItemFavorite[]}>>('favorites');
		// Leaving this for now but this has been fixed server-side, this can
		// be removed once the server is updated
		response.data.data.favorites.forEach(favorite => {
			favorite.options.forEach(option => {
				option.choices.forEach(choice => {
					choice.isUserSet = true;
				});
			});
		});
		this.setFavorites(response.data.data.favorites);
	}

	@Action
	async createFavorite(payload: {name: string; lineItem: ILineItem}) {
		const menuItem = MenuStore.item(payload.lineItem.itemId);
		if(!menuItem) {
			throw new Error(`Item not found: ${payload.lineItem.itemId}`);
		}
		const locationId = OrderStore.selectedLocationId;
		if(!locationId) {
			throw new Error('No location selected');
		}
		const favorite: IItemFavorite = {
			id: '',
			name: payload.name,
			type: ITEM_FAVORITE_TYPE.FAVORITE,
			locationId: locationId,
			customerId: '',
			itemId: menuItem.itemId,
			itemHqId: menuItem.itemHqId,
			itemName: menuItem.name,
			options: ItemService.optionsUserSelectionsMainAndContainer(payload.lineItem.options),
			createdFromId: null,
			shareUrl: null,
			created: 0,
			modified: 0
		};
		const response = await WebService.authenticatedClient.post<IBaseResponse<{favorite: IItemFavorite}>>('favorites', {
			favorite: favorite
		});
		this.addFavorite(response.data.data.favorite);
		return response.data.data.favorite.id;
	}

	@Action
	async editFavorite(params: {favoriteId: string; lineItem: ILineItem}) {
		const favorite = this.favorite(params.favoriteId);
		if(!favorite) {
			throw new Error(`Favorite not found: ${params.favoriteId}`);
		}

		const post: IItemFavorite = {...favorite, options: params.lineItem.options};

		const response = await WebService.authenticatedClient.post<IBaseResponse<{favorite: IItemFavorite}>>('favorites/edit', {
			favorite: post
		});
		this.addFavorite(response.data.data.favorite);
		return response.data.data.favorite.id;
	}

	@Action
	async deleteFavorite(id: string) {
		await WebService.authenticatedClient.post(`favorites/${id}/delete`);
		this.removeFavorite(id);
	}

	@Action
	async generateShareUrl(favoriteId: string) {
		const response = await WebService.authenticatedClient.post<IBaseResponse<{favorite: IItemFavorite}>>(`favorites/${favoriteId}/generate_share_url`);
		this.addFavorite(response.data.data.favorite);
	}

	@Action
	async fetchSharedFavorites() {
		const response = await WebService.authenticatedClient.get<IBaseResponse<{sharedFavorites: IItemFavorite[]}>>('shared_favorites');
		this.setSharedFavorites(response.data.data.sharedFavorites);
	}

	@Action
	async createSharedFavorite(hash: string) {
		const response = await WebService.authenticatedClient.post<IBaseResponse<{sharedFavorite: IItemFavorite}>>('shared_favorites', {
			hash: hash
		});
		this.addSharedFavorite(response.data.data.sharedFavorite);
		return response.data.data.sharedFavorite.id;
	}

	@Action
	async deleteSharedFavorite(id: string) {
		await WebService.authenticatedClient.post(`shared_favorites/${id}/delete`);
		this.removeSharedFavorite(id);
	}


	@Action
	async createFavoriteFromShared(payload: {sharedFavoriteId: string; name: string}) {
		const response = await WebService.authenticatedClient.post<IBaseResponse<{favorite: IItemFavorite}>>(`shared_favorites/${payload.sharedFavoriteId}/favorite`, {
			name: payload.name
		});
		this.addFavorite(response.data.data.favorite);
		return response.data.data.favorite.id;
	}

	@Mutation
	public setFavorites(favorites: IItemFavorite[]) {
		this.favorites = favorites.sort((a, b) => {
			return a.name.localeCompare(b.name);
		});
	}

	@Mutation
	public addFavorite(favorite: IItemFavorite) {
		mergeAndAdd(this.favorites, [favorite], ['id'], 'name');
	}

	@Mutation
	public removeFavorite(id: string) {
		this.favorites = this.favorites.filter(favorite => {
			return favorite.id !== id;
		});
	}

	@Mutation
	public clearFavorites() {
		this.favorites = [];
	}

	@Mutation
	public setSharedFavorites(favorites: IItemFavorite[]) {
		this.sharedFavorites = favorites.sort((a, b) => {
			return a.name.localeCompare(b.name);
		});
	}

	@Mutation
	public addSharedFavorite(favorite: IItemFavorite) {
		mergeAndAdd(this.sharedFavorites, [favorite], ['id'], 'name');
	}

	@Mutation
	public removeSharedFavorite(id: string) {
		this.sharedFavorites = this.sharedFavorites.filter(favorite => {
			return favorite.id !== id;
		});
	}
}


export {
	ItemFavoriteModule
};
